import React, { FC } from 'react';
import {
  Button,
  ButtonsContainer,
  ReferencesContainer,
  Subtitle,
  ToggleContainer,
  OptionContainer,
  Checkbox,
  NoReferencesContainer,
} from './References.style';
import { Autocomplete, TextField } from '@mui/material';
import Toggle from 'react-toggle';
import { ISegmentationClass, ISegmentationReference } from 'models/icardio';
import { labelToColor } from '../../../../utilities/labelColors';
import { useNavigate, useParams } from 'react-router-dom';

const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    padding: '6px 10px',
    fontFamily: 'inherit',
    fontSize: '14px',
    backgroundColor: '#532e63',
    color: 'white',
    borderRadius: '5px',
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: 'white',
  },
};

interface IProps {
  activeFrameIndex: number;
  activeSegmentationClass: ISegmentationClass | undefined;
  activeSegmentationReferenceClasses: string[];
  editMode: boolean;
  segmentationReferences: ISegmentationReference[];
  showSegmentationReferences: boolean;
  segmentationClasses: ISegmentationClass[];
  adoptSegmentation: () => void;
  setActiveSegmentationClass: (value: ISegmentationClass) => void;
  setActiveSegmentationReferenceClasses: (value: string[]) => void;
  setActiveTool: (tool: string) => void;
  setShowSegmentationReferences: (value: (prev: boolean) => boolean) => void;
  setActiveFrameIndex: (index: number) => void;
}

const References: FC<IProps> = ({
  activeFrameIndex,
  activeSegmentationClass,
  activeSegmentationReferenceClasses,
  editMode,
  segmentationClasses,
  segmentationReferences,
  showSegmentationReferences,
  adoptSegmentation,
  setActiveTool,
  setActiveSegmentationClass,
  setActiveSegmentationReferenceClasses,
  setShowSegmentationReferences,
  setActiveFrameIndex,
}: IProps) => {
  const { studyIcid, dicomIcid } = useParams();
  const navigate = useNavigate();

  const segmentationClassValueOptions: string[] = Array.from(
    new Set(segmentationReferences?.map((p: any) => p.segmentationClassValue) ?? [])
  )?.sort((a, b) => a.localeCompare(b)) as string[];

  const renderInput = params => <TextField {...params} label="" variant="outlined" />;

  const activeSegmentationReferenceClassValue = segmentationClassValueOptions.includes(
    activeSegmentationClass?.value ?? ''
  )
    ? (activeSegmentationClass?.value ?? '')
    : '';

  const adoptButtonIsDisabled = editMode || !activeSegmentationReferenceClassValue;

  const handleToggleShowSegmentationReferences = () => {
    if (editMode) return;
    const next = !showSegmentationReferences;
    setShowSegmentationReferences(() => next);

    if (next) {
      //   setShowSegmentationPredictions(() => false);
      setActiveTool('segmentor');

      // if activeSegmentationClasses is empty, set all possible classes
      if (activeSegmentationReferenceClasses.length === 0) {
        setActiveSegmentationReferenceClasses(segmentationClassValueOptions);
      }

      // if the activeFrameIndex is not in the references, set it to the first available
      if (!segmentationReferences.some(ref => ref.frameIndex === activeFrameIndex)) {
        setActiveFrameIndex(segmentationReferences[0].frameIndex);
        navigate(`/${studyIcid}/${dicomIcid}/${segmentationReferences[0].frameIcid}`, {
          replace: true,
        });
      }

      // ensure activeSegmentationClass is valid
      const classValues = segmentationClassValueOptions;
      const isActiveValid = classValues.includes(activeSegmentationClass?.value ?? '');

      if (!isActiveValid && classValues.length > 0) {
        const firstValue = classValues[0];
        const newActive = segmentationClasses.find(
          (sc: ISegmentationClass) => sc.value === firstValue
        );
        if (newActive) {
          setActiveSegmentationClass(newActive);
        }
      }
    }
  };

  const handleClassOnChange = (_, newValue) => {
    if (newValue) {
      const activeSegmentationClass = segmentationClasses.find((sc: any) => sc.value === newValue);
      if (activeSegmentationClass) {
        setActiveSegmentationClass(activeSegmentationClass);
      }
    }
  };

  const segmentationClassCheckBoxes = segmentationClassValueOptions.map(
    (segmentationClass: string) => {
      const handleCheckboxClick = () => {
        if (activeSegmentationReferenceClasses.includes(segmentationClass)) {
          setActiveSegmentationReferenceClasses(
            activeSegmentationReferenceClasses.filter(c => c !== segmentationClass)
          );
        } else {
          setActiveSegmentationReferenceClasses([
            ...activeSegmentationReferenceClasses,
            segmentationClass,
          ]);
        }
      };

      const isSelected = activeSegmentationReferenceClasses.includes(segmentationClass);
      const color = labelToColor(segmentationClass);

      return (
        <OptionContainer key={segmentationClass} onClick={handleCheckboxClick}>
          <Checkbox color={color} checked={isSelected} readOnly />
          {segmentationClass}
        </OptionContainer>
      );
    }
  );

  const noReferences = segmentationReferences.length === 0;

  const NoReferences = () => (
    <NoReferencesContainer>No references available for this Dicom</NoReferencesContainer>
  );

  return (
    <ReferencesContainer>
      {noReferences ? (
        <NoReferences />
      ) : (
        <>
          <Subtitle>Feature</Subtitle>
          {segmentationClassCheckBoxes}
          <ToggleContainer onClick={handleToggleShowSegmentationReferences}>
            <div style={{ color: '#fff' }}>Show references</div>
            <Toggle
              checked={showSegmentationReferences}
              onClick={handleToggleShowSegmentationReferences}
              readOnly
            />
          </ToggleContainer>
          <Subtitle>Adopt Reference</Subtitle>
          <Autocomplete
            fullWidth
            options={segmentationClassValueOptions}
            defaultValue={activeSegmentationClass?.value || ''}
            getOptionLabel={option => option}
            value={activeSegmentationReferenceClassValue}
            onChange={handleClassOnChange}
            isOptionEqualToValue={(option, value) => option === value}
            sx={autocompleteSx}
            renderInput={renderInput}
          />
          <ButtonsContainer>
            <Button
              data-testid="adopt-prediction-button"
              disabled={adoptButtonIsDisabled}
              green
              fullWidth
              onClick={adoptSegmentation}
            >
              Adopt Reference
            </Button>
          </ButtonsContainer>
        </>
      )}
    </ReferencesContainer>
  );
};

export default References;
